import { supabase } from './supabase';
import { BoardColumn, TaskCard, CreateColumnPayload, CreateCardPayload, BoardWithColumns } from '../types/board';

/**
 * Create a new column in a board
 */
export async function createColumn(
  boardId: string,
  businessId: string,
  payload: CreateColumnPayload
): Promise<BoardColumn> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Get the next position if not provided
    let position = payload.position;
    if (position === undefined) {
      const { data: lastColumn } = await supabase
        .from('board_columns')
        .select('position')
        .eq('board_id', boardId)
        .order('position', { ascending: false })
        .limit(1)
        .single();
      
      position = lastColumn ? lastColumn.position + 1 : 0;
    }

    const { data, error } = await supabase
      .from('board_columns')
      .insert({
        board_id: boardId,
        business_id: businessId,
        title: payload.title,
        position
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating column:', error);
      throw new Error(`Failed to create column: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in createColumn:', error);
    throw error;
  }
}

/**
 * Get all columns for a board with their cards
 */
export async function getBoardWithColumns(boardId: string): Promise<BoardWithColumns> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // First get the board
    const { data: board, error: boardError } = await supabase
      .from('tasks_board')
      .select('*')
      .eq('id', boardId)
      .single();

    if (boardError) {
      console.error('Error fetching board:', boardError);
      throw new Error(`Failed to fetch board: ${boardError.message}`);
    }

    // Get all columns for the board
    const { data: columns, error: columnsError } = await supabase
      .from('board_columns')
      .select('*')
      .eq('board_id', boardId)
      .order('position', { ascending: true });

    if (columnsError) {
      console.error('Error fetching columns:', columnsError);
      throw new Error(`Failed to fetch columns: ${columnsError.message}`);
    }

    // Get all cards for the board
    const { data: cards, error: cardsError } = await supabase
      .from('task_cards')
      .select('*')
      .eq('board_id', boardId)
      .order('position', { ascending: true });

    if (cardsError) {
      console.error('Error fetching cards:', cardsError);
      throw new Error(`Failed to fetch cards: ${cardsError.message}`);
    }

    // Group cards by column
    const columnsWithCards = (columns || []).map(column => ({
      ...column,
      cards: (cards || []).filter(card => card.column_id === column.id)
    }));

    return {
      ...board,
      columns: columnsWithCards
    };
  } catch (error) {
    console.error('Error in getBoardWithColumns:', error);
    throw error;
  }
}

/**
 * Update column title
 */
export async function updateColumnTitle(columnId: string, title: string): Promise<BoardColumn> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('board_columns')
      .update({ title })
      .eq('id', columnId)
      .select()
      .single();

    if (error) {
      console.error('Error updating column title:', error);
      throw new Error(`Failed to update column title: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in updateColumnTitle:', error);
    throw error;
  }
}

/**
 * Delete a column and all its cards
 */
export async function deleteColumn(columnId: string): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { error } = await supabase
      .from('board_columns')
      .delete()
      .eq('id', columnId);

    if (error) {
      console.error('Error deleting column:', error);
      throw new Error(`Failed to delete column: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in deleteColumn:', error);
    throw error;
  }
}

/**
 * Create a new card in a column
 */
export async function createCard(
  columnId: string,
  boardId: string,
  businessId: string,
  payload: CreateCardPayload
): Promise<TaskCard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Get the next position if not provided
    let position = payload.position;
    if (position === undefined) {
      const { data: lastCard } = await supabase
        .from('task_cards')
        .select('position')
        .eq('column_id', columnId)
        .order('position', { ascending: false })
        .limit(1)
        .single();
      
      position = lastCard ? lastCard.position + 1 : 0;
    }

    const { data, error } = await supabase
      .from('task_cards')
      .insert({
        column_id: columnId,
        board_id: boardId,
        business_id: businessId,
        title: payload.title,
        description: payload.description,
        status: payload.status || 'in-progress',
        urgency: payload.urgency || 'medium',
        scheduled_date: payload.scheduled_date,
        position,
        assigned_users: payload.assigned_users || []
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating card:', error);
      throw new Error(`Failed to create card: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in createCard:', error);
    throw error;
  }
}

/**
 * Update a card
 */
export async function updateCard(cardId: string, updates: Partial<TaskCard>): Promise<TaskCard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('task_cards')
      .update(updates)
      .eq('id', cardId)
      .select()
      .single();

    if (error) {
      console.error('Error updating card:', error);
      throw new Error(`Failed to update card: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in updateCard:', error);
    throw error;
  }
}

/**
 * Update column positions for drag and drop reordering
 */
export async function updateColumnPositions(
  boardId: string,
  columnUpdates: { id: string; position: number }[]
): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Update all column positions in a single transaction
    const { error } = await supabase.rpc('update_column_positions', {
      board_id: boardId,
      position_updates: columnUpdates
    });

    if (error) {
      console.error('Error updating column positions:', error);
      // Fallback to individual updates if RPC doesn't exist
      for (const update of columnUpdates) {
        const { error: updateError } = await supabase
          .from('board_columns')
          .update({ position: update.position })
          .eq('id', update.id);
        
        if (updateError) {
          console.error('Error updating column position:', updateError);
          throw new Error(`Failed to update column position: ${updateError.message}`);
        }
      }
    }
  } catch (error) {
    console.error('Error in updateColumnPositions:', error);
    throw error;
  }
}

/**
 * Update card positions within a column or move cards between columns
 */
export async function updateCardPositions(
  cardUpdates: { id: string; column_id: string; position: number }[]
): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Update all card positions in individual updates
    for (const update of cardUpdates) {
      const { error: updateError } = await supabase
        .from('task_cards')
        .update({ 
          column_id: update.column_id,
          position: update.position 
        })
        .eq('id', update.id);
      
      if (updateError) {
        console.error('Error updating card position:', updateError);
        throw new Error(`Failed to update card position: ${updateError.message}`);
      }
    }
  } catch (error) {
    console.error('Error in updateCardPositions:', error);
    throw error;
  }
}

/**
 * Delete a card
 */
export async function deleteCard(cardId: string): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { error } = await supabase
      .from('task_cards')
      .delete()
      .eq('id', cardId);

    if (error) {
      console.error('Error deleting card:', error);
      throw new Error(`Failed to delete card: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in deleteCard:', error);
    throw error;
  }
} 